// ---------------------------------------------------------------------------
// The numbers-round tile. Wider than a letter tile because a large number needs
// three digits at 800 weight, and mono so 100 and 111 are the same width.
//
// Selecting a tile moves box-shadow only, and the geometry is set in pixels with
// the border inside it, so the accent ring and the spent tile's dashed edge both
// land without resizing the tile or nudging the row. That is why the transition
// names box-shadow rather than "all": a tile that changes width as it is picked
// would reflow the board mid-tap.
//
// The phone tile is 48, down from 54, and the six pixels bought a board that
// stays on one line inside the numbers pad on a 360px phone: 6 x 48 plus five
// 4px gaps is 308 against the 312 a small phone leaves. At 54 the sixth tile
// wrapped onto a line of its own. 48 is still above every tap-target floor
// there is, and a board that reads as one row is worth more than six pixels.
//
// **The face is written in longhands, and never in the `background` shorthand.**
// A tile that is selected and then unselected used to come back with no face at
// all — a black rectangle in dark mode, a white one in light — and the cause is
// React's style diff rather than the palette. The available face was
// `background`, the selected one added `backgroundImage` on top of it; going
// back, React removes the key the new style no longer has (`backgroundImage`)
// and skips `background` because its value has not changed, so the element is
// left with a background nothing has set. React says so itself, in a warning
// this code was printing on every deselect: do not mix shorthand and
// non-shorthand properties for the same value. So every state here writes
// `backgroundImage` or `backgroundColor`, never `background`, and a state
// change is one property changing value rather than one property being removed
// out from under another.
//
// `pad` is that same argument taken one step further, for the numbers pad's
// six-column grid: the tile has NO width of its own and fills the cell it is
// given, so the row is one line at every phone width rather than at the widths
// a fixed 48 happens to fit. On a 390px phone the cell comes out at 52 and on a
// 360px one at 47, which is the same tile a fixed size would have had to pick
// the smaller of. The height is 56 rather than 48 because vertical room is the
// one thing the pad has to spend and the tile is the target being called
// clunky. Only the width is elastic: the height, the radius and the digit are
// fixed, so no tile is ever a different size from the one beside it.
// ---------------------------------------------------------------------------

import type { CSSProperties, ReactElement } from 'react';

export interface NumberTileProps {
  value: number;
  state: 'available' | 'used' | 'selected';
  /** `pad` fills its grid cell. It has no width and must not be used outside one. */
  size?: 'phone' | 'desktop' | 'pad';
  onClick?: () => void;
}

const GEOMETRY = {
  desktop: { width: 118, height: 108, radius: 12, fontSize: 40 },
  phone: { width: 48, height: 48, radius: 8, fontSize: 20 },
  pad: { width: 0, height: 56, radius: 10, fontSize: 21 },
} as const;

export function NumberTile({ value, state, size = 'phone', onClick }: NumberTileProps): ReactElement {
  const { width, height, radius, fontSize } = GEOMETRY[size];

  const face: CSSProperties = {
    width: size === 'pad' ? '100%' : width,
    height,
    borderRadius: radius,
  };
  let digitColor = 'var(--c-tile-letter)';
  let faceClass = '';

  if (state === 'used') {
    face.backgroundColor = 'var(--c-tile-used-body)';
    face.border = '2px dashed var(--c-tile-used-edge)';
    digitColor = 'var(--c-tile-used-glyph)';
  } else {
    face.backgroundImage = 'linear-gradient(var(--c-tile-top), var(--c-tile-bottom))';
    if (state === 'selected') {
      // **Selected has to be unmissable, and it may not be a fill.**
      //
      // It was a 3px accent ring around an otherwise unchanged tile, and the
      // report from the room was that the chosen value is not obvious enough —
      // which it was not: two tiles differing only in a thin outline, under a
      // thumb that is covering one of them.
      //
      // A solid accent fill is the obvious answer and it is forbidden. `accent`
      // is a light orange in dark mode and a dark one in light mode, so no
      // single ink reads on it in both themes, and `palette.test.ts` asserts
      // that `good`/`alert`/`accent` are never fills behind text. Doing it
      // properly means a new per-theme ink token solved against the fill; that
      // is worth doing one day for this and for the green `Done`, and it is not
      // worth doing in passing.
      //
      // So: everything except a fill. A thicker ring, an accent wash over the
      // face — `accent/15` over a surface is the one tint the palette test
      // already solves — the digit in accent rather than tile-letter, and the
      // tile lifted a pixel. Four signals; any one of them would be missable on
      // its own and together they cannot be.
      face.backgroundImage =
        'linear-gradient(color-mix(in oklab, var(--c-accent) 15%, var(--c-tile-top)), color-mix(in oklab, var(--c-accent) 15%, var(--c-tile-bottom)))';
      face.boxShadow =
        'inset 0 2px 0 var(--c-tile-relief-top), inset 0 -3px 0 var(--c-tile-relief-bottom), 0 0 0 4px var(--c-accent)';
      face.transform = 'translateY(-1px)';
      digitColor = 'var(--c-accent)';
    } else {
      faceClass = 'tile-relief';
    }
  }

  const digit = (
    <span
      className="font-mono leading-none font-extrabold tabular"
      style={{ fontSize, color: digitColor }}
    >
      {value}
    </span>
  );

  const motion = 'transition-[box-shadow,border-color,transform] duration-150 ease-out';
  // A grid track is minmax(0, 1fr) but a grid ITEM's own min-width is auto, so
  // without this a three-digit tile pushes its cell wider than the track and
  // the six-column row stops being six equal columns.
  const box = `grid ${size === 'pad' ? 'min-w-0' : 'shrink-0'} place-items-center`;

  if (!onClick) {
    return (
      <div
        className={`${box} ${motion} ${faceClass}`}
        style={face}
        data-tile-state={state}
      >
        {digit}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={state === 'selected'}
      className={`${box} active:translate-y-[1px] ${motion} ${faceClass}`}
      style={face}
      data-tile-state={state}
    >
      {digit}
    </button>
  );
}
